import React from "react";
import { AnalyticsAudience, AnalyticsPlatform } from "../../api/analyticsAdmin";
import { DateRangePreset } from "../../helpers/analyticsPlus";

export interface AnalyticsFilters {
  from: string;
  to: string;
  platform: AnalyticsPlatform;
  audience: AnalyticsAudience;
}

interface AnalyticsFilterBarProps {
  filters: AnalyticsFilters;
  preset: DateRangePreset;
  onPresetChange: (preset: DateRangePreset) => void;
  onCustomChange: (patch: Partial<Pick<AnalyticsFilters, "from" | "to">>) => void;
  onPlatformChange: (platform: AnalyticsPlatform) => void;
  onAudienceChange: (audience: AnalyticsAudience) => void;
}

const PRESETS: { key: DateRangePreset; label: string }[] = [
  { key: "last7", label: "Last 7 days" },
  { key: "last30", label: "Last 30 days" },
  { key: "thisMonth", label: "This month" },
  { key: "custom", label: "Custom" },
];

const PLATFORMS: AnalyticsPlatform[] = ["All", "Web", "Native", "iOS", "Android"];
const AUDIENCES: AnalyticsAudience[] = ["All", "Anonymous", "Identified"];

// Labelled group so each filter control reads clearly. Labels align to the
// bottom of the bar via the parent's items-end.
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium text-gray-500">{label}</span>
    {children}
  </div>
);

export const AnalyticsFilterBar: React.FC<AnalyticsFilterBarProps> = ({
  filters,
  preset,
  onPresetChange,
  onCustomChange,
  onPlatformChange,
  onAudienceChange,
}) => (
  <div className="flex flex-wrap items-end gap-4 mb-6">
    <Field label="Time period">
      <div className="flex gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => onPresetChange(p.key)}
            className={`px-4 py-2 text-sm rounded-lg border ${
              preset === p.key
                ? "border-primary bg-primary/10 text-primary"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </Field>

    {preset === "custom" && (
      <Field label="Custom range">
        <div className="flex gap-2">
          <input
            type="date"
            value={filters.from}
            onChange={(e) => onCustomChange({ from: e.target.value })}
            className="p-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(e) => onCustomChange({ to: e.target.value })}
            className="p-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </Field>
    )}

    <Field label="Platform">
      <select
        value={filters.platform}
        onChange={(e) => onPlatformChange(e.target.value as AnalyticsPlatform)}
        className="p-2 border border-gray-300 rounded-lg text-sm"
      >
        {PLATFORMS.map((p) => (
          <option key={p} value={p}>
            {p === "All" ? "All platforms" : p}
          </option>
        ))}
      </select>
    </Field>

    <Field label="Audience">
      <select
        value={filters.audience}
        onChange={(e) => onAudienceChange(e.target.value as AnalyticsAudience)}
        className="p-2 border border-gray-300 rounded-lg text-sm"
      >
        {AUDIENCES.map((a) => (
          <option key={a} value={a}>
            {a === "All" ? "All audiences" : a}
          </option>
        ))}
      </select>
    </Field>
  </div>
);
